import { css, useTheme } from '@emotion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { EmptyState } from '@/components/dashboard/empty-state';
import { FolderTabs } from '@/components/dashboard/folder-tabs';
import { StatsSection } from '@/components/dashboard/stats-section';
import type { StatusFilter } from '@/components/dashboard/stats-section';
import { DeleteScriptDialog } from '@/components/scripts/delete-script-dialog';
import { MoveScriptDialog } from '@/components/scripts/move-script-dialog';
import { ScriptCard } from '@/components/scripts/script-card';
import { AppErrorPage } from '@/components/ui/app-error-page';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { useFolders } from '@/hooks/use-folders';
import {
  createProject,
  deleteProject,
  duplicateProject,
  fetchProjects,
  fetchUsage,
} from '@/lib/api-client';
import type { ProjectListItem, UsageData } from '@/lib/api-client';
import { toFriendlyError } from '@/lib/utils/error-handler';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ChevronDown, Plus, Search, X } from 'lucide-react';
import { fontSizes, spacing } from '@/theme/tokens';

type SortBy = 'updatedAt' | 'createdAt' | 'title';
type SortDir = 'asc' | 'desc';

interface SortOption {
  label: string;
  by: SortBy;
  dir: SortDir;
}

const SORT_OPTIONS: SortOption[] = [
  { label: 'Last Updated', by: 'updatedAt', dir: 'desc' },
  { label: 'Title A–Z', by: 'title', dir: 'asc' },
  { label: 'Title Z–A', by: 'title', dir: 'desc' },
  { label: 'Newest', by: 'createdAt', dir: 'desc' },
  { label: 'Oldest', by: 'createdAt', dir: 'asc' },
];

const STORAGE_KEY = 'sonematic-scripts-sort';

function loadSort(): SortOption {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as SortOption;
      const valid = SORT_OPTIONS.find((o) => o.by === parsed.by && o.dir === parsed.dir);
      if (valid) return valid;
    }
  } catch {
    // ignore
  }
  return SORT_OPTIONS[0];
}

function saveSort(opt: SortOption) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ by: opt.by, dir: opt.dir }));
  } catch {
    // ignore
  }
}

export function ProjectList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFolder = searchParams.get('folder');
  const [allScripts, setAllScripts] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [sort, setSort] = useState<SortOption>(loadSort);
  const [sortOpen, setSortOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteTitle, setPendingDeleteTitle] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [moveTarget, setMoveTarget] = useState<{
    id: string;
    title: string;
    folderId: string | null;
  } | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { folders, refetch: refetchFolders } = useFolders();
  const statusFilter = (searchParams.get('status') as StatusFilter) || null;

  // Fetch ALL scripts (no folder filter) — client-side filtering
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProjects({ sortBy: sort.by, sortDir: sort.dir })
      .then((data) => {
        if (!cancelled) {
          setAllScripts(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const friendly = toFriendlyError(err);
          setError({ title: friendly.title, message: friendly.message });
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sort]);

  // Fetch usage data for the usage cards
  useEffect(() => {
    fetchUsage()
      .then(setUsage)
      .catch((err) => {
        console.error('Failed to load usage data:', err);
      });
  }, []);

  // Client-side filter by active folder (server already sorts) — fed to StatsSection
  const folderFilteredScripts = useMemo(() => {
    if (!activeFolder) return allScripts;
    return allScripts.filter((s) => s.folderId === activeFolder);
  }, [allScripts, activeFolder]);

  // Status filter applied after folder
  const statusFilteredScripts = useMemo(() => {
    if (!statusFilter) return folderFilteredScripts;
    if (statusFilter === 'ready') {
      return folderFilteredScripts.filter((s) => s.pipelineStatus === 'ready');
    }
    return folderFilteredScripts.filter((s) =>
      ['needs_analysis', 'needs_rehearsal', 'needs_music'].includes(s.pipelineStatus),
    );
  }, [folderFilteredScripts, statusFilter]);

  // Search filter applied after status
  const filteredScripts = useMemo(() => {
    if (!searchQuery.trim()) return statusFilteredScripts;
    const q = searchQuery.trim().toLowerCase();
    return statusFilteredScripts.filter((s) => s.title.toLowerCase().includes(q));
  }, [statusFilteredScripts, searchQuery]);

  // Close sort dropdown on outside click
  useEffect(() => {
    if (!sortOpen) return;
    function handleClick(e: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [sortOpen]);

  const handleFolderChange = useCallback(
    (folderId: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (folderId) {
        next.set('folder', folderId);
      } else {
        next.delete('folder');
      }
      setSearchParams(next);
      setSearchQuery('');
    },
    [searchParams, setSearchParams],
  );

  const handleStatusFilterChange = useCallback(
    (filter: StatusFilter) => {
      const next = new URLSearchParams(searchParams);
      if (filter) {
        next.set('status', filter);
      } else {
        next.delete('status');
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  // Cmd+K / Ctrl+K to focus search
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clean up audio on unmount / navigation
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlayToggle = useCallback(
    (id: string) => {
      if (playingId === id) {
        // Stop current
        audioRef.current?.pause();
        audioRef.current = null;
        setPlayingId(null);
        return;
      }

      // Stop current if different
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      const script = allScripts.find((s) => s.id === id);
      if (!script?.previewAudioUrl) return;

      const audio = new Audio(script.previewAudioUrl);
      audio.addEventListener('ended', () => {
        setPlayingId(null);
        audioRef.current = null;
      });
      audio.play().catch(() => {
        setPlayingId(null);
      });
      audioRef.current = audio;
      setPlayingId(id);
    },
    [playingId, allScripts],
  );

  const handleDuplicate = useCallback(
    async (id: string) => {
      try {
        const project = await duplicateProject(id);
        addToast({ variant: 'success', title: 'Script duplicated', message: '' });
        navigate(`/app/projects/${project.id}/edit`);
      } catch {
        addToast({ variant: 'error', title: 'Failed to duplicate script', message: '' });
      }
    },
    [navigate, addToast],
  );

  const handleSortSelect = useCallback((opt: SortOption) => {
    setSort(opt);
    saveSort(opt);
    setSortOpen(false);
  }, []);

  const handleDelete = useCallback((id: string, title: string) => {
    setPendingDeleteId(id);
    setPendingDeleteTitle(title);
  }, []);

  const handleMove = useCallback((id: string, title: string, folderId: string | null) => {
    setMoveTarget({ id, title, folderId });
  }, []);

  const handleMoved = useCallback(() => {
    setMoveTarget(null);
    addToast({ variant: 'success', title: 'Script moved', message: '' });
    setSort((s) => ({ ...s }));
    refetchFolders();
  }, [addToast, refetchFolders]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDeleteId) return;
    setIsDeleting(true);
    try {
      await deleteProject(pendingDeleteId);
      setAllScripts((prev) => prev.filter((s) => s.id !== pendingDeleteId));
      addToast({ variant: 'success', title: 'Script deleted', message: '' });
      setPendingDeleteId(null);
      refetchFolders();
    } catch {
      addToast({ variant: 'error', title: 'Failed to delete script', message: '' });
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDeleteId, addToast, refetchFolders]);

  const handleDeleteCancel = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const handleNewScript = useCallback(async () => {
    try {
      const project = await createProject('New Script', activeFolder);
      navigate(`/app/projects/${project.id}/edit`);
    } catch {
      addToast({ variant: 'error', title: 'Failed to create script', message: '' });
    }
  }, [navigate, activeFolder, addToast]);

  const hasActiveFilters = !!statusFilter || !!searchQuery.trim();

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const statusFilterLabel =
    statusFilter === 'ready'
      ? 'Ready to Present'
      : statusFilter === 'needs_music'
        ? 'Needs Music'
        : '';

  return (
    <main
      css={css`
        max-width: 900px;
        margin: 0 auto;
        width: 100%;
      `}
    >
      <div
        css={css`
          padding: ${spacing.xl} ${spacing.lg} ${spacing.lg};
        `}
      >
        {!loading && !error && (
          <StatsSection
            scripts={activeFolder ? folderFilteredScripts : allScripts}
            usage={usage}
            activeStatusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
          />
        )}

        <div
          css={css`
            margin-top: ${spacing.lg};
          `}
        >
          <FolderTabs
            folders={folders}
            activeFolder={activeFolder}
            onFolderChange={handleFolderChange}
            onFoldersChanged={refetchFolders}
            totalScriptCount={allScripts.length}
          />
        </div>

        <header
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: ${spacing.md};
            margin-bottom: ${spacing.md};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.md};
            `}
          >
            <h1
              css={css`
                margin: 0;
                font-size: ${fontSizes.xl};
                font-weight: 500;
                color: ${theme.colors.textSecondary};
              `}
            >
              {activeFolder
                ? (folders.find((f) => f.id === activeFolder)?.name ?? 'Folder')
                : 'All Scripts'}
            </h1>

            <div
              ref={sortRef}
              css={css`
                position: relative;
              `}
            >
              <button
                type="button"
                onClick={() => setSortOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={sortOpen}
                css={css`
                  font-size: ${fontSizes.sm};
                  color: ${theme.colors.text};
                  background: ${theme.colors.surface};
                  border: 1px solid ${theme.colors.border};
                  border-radius: 6px;
                  padding: ${spacing.xs} ${spacing.sm};
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  gap: ${spacing.xs};
                  font-weight: 500;
                  transition: border-color 0.2s ease-in-out;
                  &:hover {
                    border-color: ${theme.colors.textSecondary};
                  }
                `}
              >
                <ArrowUpDown size={14} aria-hidden="true" />
                {sort.label}
                <ChevronDown size={14} aria-hidden="true" />
              </button>
              {sortOpen && (
                <ul
                  role="menu"
                  css={css`
                    position: absolute;
                    right: 0;
                    top: calc(100% + 4px);
                    background: ${theme.colors.surfaceElevated};
                    border: 1px solid ${theme.colors.border};
                    border-radius: 8px;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                    padding: ${spacing.xs} 0;
                    list-style: none;
                    margin: 0;
                    min-width: 160px;
                    z-index: 10;
                  `}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <li key={`${opt.by}-${opt.dir}`} role="none">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleSortSelect(opt)}
                        css={css`
                          display: block;
                          width: 100%;
                          text-align: left;
                          padding: ${spacing.xs} ${spacing.md};
                          background: ${sort.by === opt.by && sort.dir === opt.dir
                            ? theme.colors.sidebarActiveBg
                            : 'transparent'};
                          color: ${sort.by === opt.by && sort.dir === opt.dir
                            ? theme.colors.sidebarActiveText
                            : theme.colors.text};
                          border: none;
                          cursor: pointer;
                          font-size: ${fontSizes.sm};
                          &:hover {
                            background: ${theme.colors.sidebarHoverBg};
                          }
                        `}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div
              css={css`
                position: relative;
                display: flex;
                align-items: center;
              `}
            >
              <Search
                size={14}
                aria-hidden="true"
                css={css`
                  position: absolute;
                  left: 8px;
                  color: ${theme.colors.textSecondary};
                  pointer-events: none;
                `}
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search scripts... ⌘K"
                aria-label="Search scripts"
                css={css`
                  font-size: ${fontSizes.sm};
                  color: ${theme.colors.text};
                  background: ${theme.colors.surface};
                  border: 1px solid ${theme.colors.border};
                  border-radius: 6px;
                  padding: ${spacing.xs} ${spacing.sm};
                  padding-left: 28px;
                  padding-right: ${searchQuery ? '28px' : spacing.sm};
                  width: 180px;
                  outline: none;
                  transition: border-color 0.2s ease-in-out;
                  &:focus {
                    border-color: ${theme.colors.primary};
                  }
                  &::placeholder {
                    color: ${theme.colors.textSecondary};
                  }
                `}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  css={css`
                    position: absolute;
                    right: 4px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: ${theme.colors.textSecondary};
                    padding: 2px;
                    display: flex;
                    align-items: center;
                    &:hover {
                      color: ${theme.colors.text};
                    }
                  `}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <Button
            size="sm"
            onClick={handleNewScript}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.sm};
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              min-height: 44px;

              &:hover {
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              }
            `}
          >
            <Plus size={16} aria-hidden="true" />
            New Script
          </Button>
        </header>

        {loading && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.md};
            `}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                css={css`
                  background: ${theme.colors.surface};
                  border: 1px solid ${theme.colors.border};
                  border-radius: 8px;
                  padding: ${spacing.md} ${spacing.lg};
                `}
              >
                <Skeleton height="1.25rem" width="60%" />
                <div
                  css={css`
                    margin-top: ${spacing.sm};
                  `}
                >
                  <Skeleton height="0.875rem" width="40%" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <AppErrorPage
            title={error.title}
            message={error.message}
            onRetry={() => setSort((s) => ({ ...s }))}
          />
        )}

        {!loading && !error && allScripts.length === 0 && (
          <EmptyState onCreateScript={handleNewScript} />
        )}

        {!loading && !error && hasActiveFilters && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.sm};
              margin-bottom: ${spacing.md};
              flex-wrap: wrap;
            `}
          >
            {statusFilter && (
              <span
                css={css`
                  display: inline-flex;
                  align-items: center;
                  gap: ${spacing.xs};
                  padding: ${spacing.xs} ${spacing.sm};
                  background: ${theme.colors.sidebarActiveBg};
                  color: ${theme.colors.text};
                  border-radius: 980px;
                  font-size: ${fontSizes.sm};
                `}
              >
                {statusFilterLabel}
                <button
                  type="button"
                  onClick={() => handleStatusFilterChange(null)}
                  aria-label="Remove status filter"
                  css={css`
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: ${theme.colors.textSecondary};
                    padding: 0;
                    display: flex;
                    align-items: center;
                    &:hover {
                      color: ${theme.colors.text};
                    }
                  `}
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </span>
            )}
          </div>
        )}

        {!loading && !error && allScripts.length > 0 && filteredScripts.length === 0 && (
          <div
            css={css`
              color: ${theme.colors.textSecondary};
              font-size: ${fontSizes.md};
              text-align: center;
              padding: ${spacing.xl} 0;
            `}
          >
            <p>No matching scripts found.</p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                css={css`
                  margin-top: ${spacing.sm};
                  background: none;
                  border: none;
                  color: ${theme.colors.primary};
                  cursor: pointer;
                  font-size: ${fontSizes.sm};
                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {!loading && !error && filteredScripts.length > 0 && (
          <ul
            className="fade-in"
            css={css`
              list-style: none;
              padding: 0;
              margin: 0;
              display: flex;
              flex-direction: column;
              gap: 12px;
            `}
          >
            {filteredScripts.map((script) => (
              <ScriptCard
                key={script.id}
                script={script}
                onDelete={handleDelete}
                onMove={handleMove}
                onDuplicate={handleDuplicate}
                onPlayToggle={handlePlayToggle}
                isPlaying={playingId === script.id}
              />
            ))}
          </ul>
        )}

        <DeleteScriptDialog
          scriptTitle={pendingDeleteTitle}
          isOpen={pendingDeleteId !== null}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
        />

        {moveTarget && (
          <MoveScriptDialog
            isOpen
            scriptId={moveTarget.id}
            scriptTitle={moveTarget.title}
            currentFolderId={moveTarget.folderId}
            onMoved={handleMoved}
            onCancel={() => setMoveTarget(null)}
          />
        )}
      </div>
    </main>
  );
}
